<template>
  <div class="main-nav" data-test-id="main-nav">
    <EIconButton
      ref="toggle"
      aria-label="Toggle menu"
      data-track-id="burger-menu"
      data-test-id="main-nav-toggle"
      icon="hamburger"
      class="icon-button"
      :variant="withPanel ? 'bare' : 'outline-dark'"
      @click="toggle"
    />
    <EPanel
      v-if="withPanel"
      v-on-clickaway="clickAway"
      :visible="visible"
      :side="panelSide"
      backdrop
      data-test-id="main-nav-panel"
      @click="hide"
    >
      <div class="panel-header">
        <EIcon icon="logo-compact" class="panel-header__icon" />
        <EIconButton
          icon="close"
          aria-label="Close"
          class="panel-close"
          variant="bare"
          data-test-id="main-nav-panel-close"
          @click="hide"
        />
      </div>
      <EMainNavMenu
        :translation-string-prefix="translationStringPrefix"
        :translation-string-sufix="translationStringSufix"
      />
    </EPanel>
    <EDropdown
      v-else
      v-on-clickaway="clickAway"
      :visible="visible"
      class="main-nav__dropdown"
      side="right"
      data-test-id="main-nav-dropdown"
    >
      <EMainNavMenu
        :translation-string-prefix="translationStringPrefix"
        :translation-string-sufix="translationStringSufix"
        :settings-url="settingsUrl"
      />
    </EDropdown>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import debounce from 'lodash/debounce';

import { isDesktopUp } from '~~/common/js/client/screen';
import { RESIZE_DEBOUNCE_DELAY } from '~~/common/js/constants/events';
import EDropdown from '~~/common/vue/components/dropdown';
import EIcon from '~~/common/vue/components/icon';
import EIconButton from '~~/common/vue/components/icon-button';
import EPanel from '~~/common/vue/components/panel';
import EMainNavMenu from './menu';

export default {
  name: 'EMainNav',
  components: {
    EDropdown,
    EIcon,
    EIconButton,
    EMainNavMenu,
    EPanel,
  },
  mixins: [clickaway],
  props: {
    /* eslint-disable vue/require-default-prop */
    panelSide: EPanel.props.side,
    translationStringPrefix: EMainNavMenu.props.translationStringPrefix,
    translationStringSufix: EMainNavMenu.props.translationStringSufix,
    settingsUrl: EMainNavMenu.props.settingsUrl,
    /* eslint-enabled vue/require-default-prop */
  },
  data() {
    return {
      visible: false,
      withPanel: false,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
  },
  mounted() {
    document.addEventListener('keyup', this.onKeyUp);
    this.onResize = debounce(this.setWithPanel, RESIZE_DEBOUNCE_DELAY);
    window.addEventListener('resize', this.onResize);
    this.setWithPanel();
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.onKeyUp);
  },
  methods: {
    clickAway({ target }) {
      const { $el } = this.$refs.toggle;
      if (target !== $el && !$el.contains(target)) {
        this.hide();
      }
    },
    setVisible(value) {
      this.visible = value;
      if (this.visible) {
        this.$emit('visible');
      }
    },
    hide() {
      this.setVisible(false);
    },
    onKeyUp({ which, keyCode }) {
      const code = keyCode || which;
      if (code === 27) {
        this.hide();
      }
    },
    toggle(event) {
      this.setVisible(!this.visible);
      if (this.visible) {
        event.currentTarget.focus();
      } else {
        event.currentTarget.blur();
      }
    },
    setWithPanel() {
      this.withPanel = !isDesktopUp();
    },
  },
};
</script>

<style lang="scss" scoped>
.panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: $space-s;
  height: $space-4l + $space-s + $space-2s;
}

.panel-header__icon {
  height: $space-1l;
}

.icon-button {
  height: $space-3l;
  min-width: $space-3l;
  border-radius: $border-radius-full;
  padding: 0;
  border-width: 0;

  @include desktop {
    border-width: $border-width;
  }

  &:hover {
    background-color: $white;
    border-color: $color-border-hover-secondary;
  }

  &:focus {
    outline: none;
    border-color: $transparent;
    background-color: $gray-20;

    &:hover {
      // special case where design specifies the same border color on hover and focus
      border-color: $color-border;
    }
  }
}

.main-nav {
  position: relative;
}

.main-nav__dropdown {
  margin-top: $space-s;
  right: -$space-s;
}
</style>

<style lang="scss">
.icon-button:hover,
.icon-button:focus {
  .icon-button__icon-hamburger path {
    fill: $gray-70;
  }
}
</style>

<docs>
```js
<EMainNav />
```
</docs>
